import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import type { Notification, NotificationListResponse } from "../lib/notifications.js";

/**
 * Full-page notifications surface (ADR-0044, UX Pattern C).
 * Spec: governance-foundation §13.3.
 *
 * Route: `/app/notifications?tab=inbox|trash`
 *
 * Two tabs: Inbox (unread + read) and Trash (dismissed, not yet purged).
 * The drawer (NotificationBell) remains the quick-glance surface; this
 * page is the triage/workflow surface for heavier volume.
 */

type Tab = "inbox" | "trash";

function tabFromSearch(raw: string | null): Tab {
  return raw === "trash" ? "trash" : "inbox";
}

/**
 * Resolve the click-through target for a notification row. Known entity
 * types deep-link; unknown types (or entity-less rows like `system_alert`)
 * fall back to the notification's own detail page so every row is clickable.
 * Keep the switch identical to `NotificationBell.tsx`'s map.
 */
function linkForNotification(n: Pick<Notification, "id" | "entity_type" | "entity_id">): string {
  if (n.entity_type && n.entity_id) {
    switch (n.entity_type) {
      case "action_item":
        return `/app/action-items/${n.entity_id}`;
    }
  }
  return `/app/notifications/${n.id}`;
}

export function Notifications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromSearch(searchParams.get("tab"));
  const toast = useToast();
  const { confirm } = useConfirm();

  const setTab = useCallback((next: Tab) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", next);
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return (
    <PageLayout title="Notifications">
      <div className="tabs">
        <button type="button" className={`tab${tab === "inbox" ? " tabActive" : ""}`} onClick={() => setTab("inbox")}>
          Inbox
        </button>
        <button type="button" className={`tab${tab === "trash" ? " tabActive" : ""}`} onClick={() => setTab("trash")}>
          Trash
        </button>
      </div>
      <div className="tabPanel">
        {tab === "inbox" && <InboxTab toast={toast} />}
        {tab === "trash" && <TrashTab toast={toast} confirm={confirm} />}
      </div>
    </PageLayout>
  );
}

// ---------------------------------------------------------------------------
// Inbox tab — active notifications (unread + read)
// ---------------------------------------------------------------------------
function InboxTab({ toast }: { toast: ReturnType<typeof useToast> }) {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (unreadOnly) params.set("status", "unread");
      const list = await apiFetch<NotificationListResponse>(`/api/tenant/notifications?${params.toString()}`);
      setItems(list.items);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => { void load(); }, [load]);

  async function dismissOne(id: string) {
    const prev = items;
    setItems((rows) => rows.filter((r) => r.id !== id));
    try {
      await apiFetch(`/api/tenant/notifications/${id}`, { method: "PATCH", body: { status: "dismissed" } });
    } catch (e) {
      setItems(prev);
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function markAllRead() {
    setBusy(true);
    try {
      await apiFetch("/api/tenant/notifications/mark-all-read", { method: "POST" });
      await load();
      toast.push({ kind: "success", message: "Marked all as read." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function dismissAll() {
    setBusy(true);
    try {
      const res = await apiFetch<{ updated: number }>("/api/tenant/notifications/dismiss-all", { method: "POST" });
      await load();
      toast.push({ kind: "success", message: `Dismissed ${res.updated} notifications.` });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const unreadCount = useMemo(() => items.filter((i) => i.status === "unread").length, [items]);

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="toolbarGroup">
          <button
            type="button"
            className={`pill ${unreadOnly ? "pillOk" : "pillNeutral"}`}
            style={{ cursor: "pointer", border: 0 }}
            onClick={() => setUnreadOnly(false)}
          >All</button>
          <button
            type="button"
            className={`pill ${unreadOnly ? "pillNeutral" : "pillOk"}`}
            style={{ cursor: "pointer", border: 0 }}
            onClick={() => setUnreadOnly(true)}
          >Unread only</button>
        </div>
        <div className="toolbarGroup" style={{ marginLeft: "auto" }}>
          <button type="button" className="btn" onClick={() => void markAllRead()} disabled={busy || unreadCount === 0}>
            Mark all read
          </button>
          <button type="button" className="btn btnDanger" onClick={() => void dismissAll()} disabled={busy || items.length === 0}>
            Dismiss all
          </button>
        </div>
      </div>

      {error && <ErrorState detail={error} />}
      {loading && <LoadingState />}

      {!loading && !error && (
        <div className="card tableWrap">
          {items.length === 0 ? (
            <div className="adminListEmpty">
              {unreadOnly ? "No unread notifications." : "You're all caught up."}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th style={{ textAlign: "center", width: 120 }}>Category</th>
                  <th style={{ width: 160 }}>Received</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((n) => <NotificationListRow key={n.id} n={n} onDismiss={() => void dismissOne(n.id)} />)}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}

function NotificationListRow({ n, onDismiss }: { n: Notification; onDismiss: () => void }) {
  const to = linkForNotification(n);
  const isUnread = n.status === "unread";
  return (
    <tr style={{ background: isUnread ? "var(--surface-2, rgba(0,0,0,0.03))" : undefined }}>
      <td>
        <Link to={to} style={{ color: "inherit", textDecoration: "none", display: "block" }}>
          <div style={{ fontWeight: isUnread ? 700 : 500 }}>{n.title}</div>
          <div className="muted" style={{ fontSize: 12 }}>{truncate(n.body, 200)}</div>
        </Link>
      </td>
      <td style={{ textAlign: "center" }}>
        <span className={`pill ${categoryPillClass(n.category)}`}>{n.category}</span>
      </td>
      <td className="muted" style={{ fontSize: 13 }}>
        {new Date(n.created_at).toLocaleString()}
      </td>
      <td>
        <button type="button" className="adminListAction" onClick={onDismiss}>Dismiss</button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Trash tab — dismissed notifications
// ---------------------------------------------------------------------------
function TrashTab({ toast, confirm }: { toast: ReturnType<typeof useToast>; confirm: ReturnType<typeof useConfirm>["confirm"] }) {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiFetch<NotificationListResponse>("/api/tenant/notifications/trash?limit=200");
      setItems(list.items);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function purgeOne(id: string) {
    const prev = items;
    setItems((rows) => rows.filter((r) => r.id !== id));
    try {
      await apiFetch(`/api/tenant/notifications/trash/${id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Purged." });
    } catch (e) {
      setItems(prev);
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function restoreOne(id: string) {
    const prev = items;
    setItems((rows) => rows.filter((r) => r.id !== id));
    try {
      await apiFetch(`/api/tenant/notifications/trash/${id}/restore`, { method: "POST" });
      toast.push({ kind: "success", message: "Restored to inbox." });
    } catch (e) {
      setItems(prev);
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function purgeAll() {
    const ok = await confirm({
      title: "Purge all trash?",
      detail: "Dismissed notifications will be deleted permanently.",
      confirmLabel: "Purge all"
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await apiFetch<{ purged: number }>("/api/tenant/notifications/trash", { method: "DELETE" });
      setItems([]);
      toast.push({ kind: "success", message: `Purged ${res.purged} notifications.` });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button
          type="button"
          className="btn btnDanger"
          onClick={() => void purgeAll()}
          disabled={busy || items.length === 0}
        >
          Purge all
        </button>
      </div>

      {error && <ErrorState detail={error} />}
      {loading && <LoadingState />}

      {!loading && !error && (
        <div className="card tableWrap">
          {items.length === 0 ? (
            <div className="adminListEmpty">Trash is empty.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th style={{ textAlign: "center", width: 120 }}>Category</th>
                  <th style={{ width: 180 }}>Dismissed</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((n) => (
                  <tr key={n.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{n.title}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{truncate(n.body, 200)}</div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span className={`pill ${categoryPillClass(n.category)}`}>{n.category}</span>
                    </td>
                    <td className="muted" style={{ fontSize: 13 }}>
                      {n.dismissed_at ? new Date(n.dismissed_at).toLocaleString() : "—"}
                    </td>
                    <td>
                      <button type="button" className="adminListAction" onClick={() => void restoreOne(n.id)}>
                        Restore
                      </button>
                      <button type="button" className="adminListAction isDanger" onClick={() => void purgeOne(n.id)} style={{ marginLeft: 8 }}>
                        Purge
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}

function categoryPillClass(category: string): string {
  switch (category) {
    case "system": return "pillWarn";
    case "digest": return "pillNeutral";
    default: return "pillNeutral";
  }
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}
